import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Dropdown, Form, Row, Spinner } from 'react-bootstrap'
import { Fetcher } from '../../../Helpers/fetcher'
import ToolkitProvider, { CSVExport, Search } from 'react-bootstrap-table2-toolkit'
import paginationFactory, { PaginationListStandalone, PaginationProvider, SizePerPageDropdownStandalone } from 'react-bootstrap-table2-paginator'
import BootstrapTable from 'react-bootstrap-table-next'
import { TEMPLATES, USER_ROLES } from '../../../Helpers/constant'
import { convertToAMPM, formatDate } from '../../../Helpers/utils'
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Notify } from '../../../Helpers/notification'

const formatDateTime = (cell) => {
    if (!cell) return "-";

    const date = new Date(cell.replace(" ", "T"));

    if (isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true
    });
};

const CvcSummary = ({ app }) => {
    const [cvcSummaryList, setCvcSummaryList] = useState([])
    const [startDate, setStartDate] = useState("")
    const [endDate, setEndDate] = useState("")
    const [loading, setLoading] = useState(false);

    const isSubOrSuperAdmin = app.isSubOrSuperAdmin
    

    const getCvcSummary = () => {
        const apiPath = app.apiPath;
        const userId = app.userId;
        setLoading(true);
        Fetcher.post(`${apiPath}/api/obd/continous/voice/campaign/summary`, {
            method: "POST",
            headers: { "Content-type": "application/json" },
            body: JSON.stringify({
                userId,
                endDate: formatDate(endDate),
                startDate: formatDate(startDate)
            })
        }).then(res => {
            const data = Array.isArray(res) ? res : []
            setCvcSummaryList(data)
        }).finally(() => setLoading(false))
    }

    const handleSearch = (e) => {
        e.preventDefault();
        getCvcSummary();
    }

    const generateReport = async(campaignId, reportType, scheduleDate) => {
        const data = {
            "campaignId": campaignId,
            "reportType": reportType,
            "scheduleDate": scheduleDate
        };
        return Fetcher.post(app.apiPath + '/api/obd/report/generate', { headers: { "Content-type": "application/json" }, method: 'POST', body: JSON.stringify(data) })
            .then(res => {
                if (res.message) {
                    Notify.notify(res.message);
                }
            })
            .catch(err => { console.log('Error in Generating Report', err) });
    }

    useEffect(() => {
        getCvcSummary()
    }, [])

    const columns = [
        {
            dataField: "campaignId",
            text: "Campaign ID",
            sort: true
        },
        {
            dataField: "campaignName",
            text: "Campaign Name",
            sort: true
        },
        {
            dataField: "username",
            text: "User Name",
            sort: true
        },
        {
            dataField: "campaignType",
            text: "Campaign Type",
            sort: true,
        },
        {
            dataField: "userId",
            text: "User ID",
            sort: true,
            hidden: !isSubOrSuperAdmin
        },
        {
            dataField: "scheduleDate",
            text: "Schedule Date",
            sort: true,
        },
        {
            dataField: "startTime",
            text: "Start Time",
            sort: true,
        },
        {
            dataField: "endDate",
            text: "End Date",
            sort: true,
        },
        {
            dataField: "endtime",
            text: "End Time",
            sort: true,
        },
        {
            dataField: "reportDate",
            text: "Report Date",
            sort: true
        },
        {
            dataField: "totalNumbers",
            text: "Total Numbers",
            sort: true
        },
        {
            dataField: "totalDnd",
            text: "Total DnD",
            sort: true
        },
        {
            dataField: "totalDialed",
            text: "Total Dialed",
            sort: true
        },
        {
            dataField: "totalConnectedCalls",
            text: "Connected Calls",
            sort: true
        },
        {
            dataField: "totalFailedCalls",
            text: "Failed Calls",
            sort: true
        },
        {
            dataField: "totalduration",
            text: "Total Duration",
            sort: true
        },
        {
            dataField: "totalPulseduration",
            text: "Pulse Duration",
            sort: true
        },
        {
            dataField: "totalPulses",
            text: "Total Pulses",
            sort: true
        },
        {
            dataField: "action",
            text: "Action",
            formatter: (cell, row) => {
                return (
                    <Dropdown>
                        <Dropdown.Toggle variant="primary" className='btn-sm' id={`dropdown-${row.campaignId}`}>
                            Take Action
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={() => generateReport(row.campaignId, "continuous", row.reportDate)}
                            >
                                Generate Report
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                );
            },
            headerStyle: { width: "150px" }
        }
    ];

    const data = useMemo(() => {
        return cvcSummaryList.map((cvc) => ({
            ...cvc,
            campaignType: TEMPLATES[cvc.campaignType] ?? "",
            scheduleDate: formatDateTime(cvc.scheduleDate),
            startTime: convertToAMPM(cvc.startTime),
            endDate: formatDateTime(cvc.endDate),
            endtime: convertToAMPM(cvc.endtime),
            username: cvc.username ?? "",
        }));
    }, [cvcSummaryList]);

    const { SearchBar, ClearSearchButton } = Search;
    const { ExportCSVButton } = CSVExport;

    const defaultSorted = [{
        dataField: 'endtime',
        order: 'desc'
    }];

    const paginationOption = {
        custom: true,
        totalSize: cvcSummaryList.length,
        sizePerPage: 10,
        sizePerPageList: [10, 25, 50, 100],
    };
    return (
        <Card>
            <Card.Header>CVC Summary</Card.Header>
            <Card.Body>
                <Form className="add-campaign-form" method='post' onSubmit={handleSearch}>
                    <Row className="align-items-end g-2 mb-3">
                        <Col md="auto">
                            <Form.Group>
                                <Form.Label className="mb-1">Start Date</Form.Label>
                                <DatePicker
                                    className="form-control "
                                    dateFormat="yyyy-MM-dd"
                                    selected={startDate}
                                    onChange={setStartDate}
                                    placeholderText="Start date"
                                />
                            </Form.Group>
                        </Col>

                        <Col md="auto">
                            <Form.Group>
                                <Form.Label className="mb-1">End Date</Form.Label>
                                <DatePicker
                                    className="form-control "
                                    dateFormat="yyyy-MM-dd"
                                    selected={endDate}
                                    onChange={setEndDate}
                                    placeholderText="End date"
                                />
                            </Form.Group>
                        </Col>

                        <Col md="auto">
                            <Button type="submit">
                                Search
                            </Button>
                        </Col>
                    </Row>
                </Form>
                <div className="user-list-table-cont">
                    {loading ? (
                        <div className="text-center">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <PaginationProvider pagination={paginationFactory(paginationOption)}>
                            {
                                ({
                                    paginationProps,
                                    paginationTableProps
                                }) => (
                                    <ToolkitProvider
                                        keyField="id"
                                        data={data}
                                        columns={columns}
                                        search
                                        bootstrap4
                                        exportCSV={{ fileName: 'CVC_summary.csv' }}
                                    >
                                        {
                                            props => (
                                                <div className="">
                                                    Show <SizePerPageDropdownStandalone className="search-dropdown" {...paginationProps} /> items
                                                    <span className="search-cont"><SearchBar {...props.searchProps} /></span>
                                                    <ClearSearchButton className="btn-primary btn-round" {...props.searchProps} />
                                                    <ExportCSVButton className="export-csv btn-primary ml-2" {...props.csvProps}>Export to CSV</ExportCSVButton>
                                                    <hr />
                                                    <BootstrapTable
                                                        bordered={true}
                                                        defaultSorted={defaultSorted}
                                                        striped={true}
                                                        condensed
                                                        wrapperClasses="table-responsive"
                                                        classes="userCampaignList-table"
                                                        {...props.baseProps}
                                                        {...paginationTableProps}
                                                        noDataIndication={"No CVC Summary to show"}
                                                    />
                                                    <PaginationListStandalone {...paginationProps} />
                                                </div>
                                            )
                                        }
                                    </ToolkitProvider>
                                )
                            }
                        </PaginationProvider>
                    )}
                </div>
            </Card.Body>
        </Card>
    )
}

export default CvcSummary